import { Link, useLocation } from "react-router-dom";
import { DonkeyFaceMark } from "@/components/Logo";
import { ExternalLink, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";

// Fire-and-forget, aggregate-only click log. Never blocks or delays navigation:
// uses keepalive and swallows every error. No personal data is sent.
function trackCrossPromo(business, placement) {
  try {
    fetch(`${API}/cross-promo/click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ business, placement }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* never break navigation for analytics */ }
}

const INTERNAL = [
  { label: "How it works", to: "/how-it-works" },
  { label: "Example verdict", to: "/example" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const LEGAL = [
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Cookie Policy", to: "/cookies" },
  { label: "Accessibility", to: "/accessibility" },
];

function Ext({ href, children, className = "" }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 transition-colors ${className}`}>
      {children}
    </a>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();
  const { pathname } = useLocation();
  const placement = `footer:${pathname}`;
  return (
    <footer className="border-t border-donkey-line bg-donkey-black" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4">
            <div className="flex items-center gap-2.5 mb-4">
              <DonkeyFaceMark size={38} />
              <span className="font-heading font-bold tracking-tight text-donkey-warm text-base flex items-center gap-1.5">
                The Honest Donkey <span className="w-1.5 h-1.5 rounded-full bg-donkey-orange" />
              </span>
            </div>
            <p className="text-sm font-heading text-donkey-warm/90 mb-2">Find, build and grow a business—<span className="text-donkey-orange">without the bullshit.</span></p>
            <p className="text-sm text-donkey-grey max-w-xs leading-relaxed">
              Discover, test and build a business grounded in your capabilities, circumstances and real market evidence.
            </p>
          </div>

          {/* Product */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-donkey-grey mb-4">Product</h4>
            <ul className="space-y-3">
              {INTERNAL.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-donkey-warm/80 hover:text-donkey-orange transition-colors" data-testid={`footer-${l.to.replace("/", "")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-donkey-grey mb-4">Legal</h4>
            <ul className="space-y-3">
              {LEGAL.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-donkey-warm/80 hover:text-donkey-orange transition-colors" data-testid={`footer-${l.to.replace("/", "")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Elsewhere — branded cards */}
          <div className="col-span-2 md:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.2em] text-donkey-grey mb-4">Elsewhere</h4>
            {/* Missiora — featured career-support card */}
            <a href="https://www.missiora.com" target="_blank" rel="noopener noreferrer"
              data-testid="footer-missiora-card"
              onClick={() => trackCrossPromo("missiora", placement)}
              className="group block rounded-2xl p-4 mb-3 border border-[#6b6bf0]/25 bg-gradient-to-br from-[#131333] to-donkey-black transition-all duration-300 hover:-translate-y-0.5 hover:border-[#8f8ff5]/70 hover:shadow-[0_10px_30px_rgba(107,107,240,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-donkey-orange focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-black">
              <div className="flex items-start gap-3">
                <img src="/brand/missiora-full.png" alt="Missiora logo" className="w-11 h-11 rounded-full shrink-0" />
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-donkey-warm text-sm">Missiora</p>
                  <p className="text-xs text-donkey-grey leading-relaxed mt-0.5">AI-powered career readiness and employability intelligence.</p>
                  <p className="text-xs text-donkey-warm/70 leading-relaxed mt-1.5">If building a business is not your next move, strengthen your route into employment.</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#9c9cf7] group-hover:text-[#b9b9fb] transition-colors">
                    Explore Missiora <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /> <ExternalLink className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </a>
            {/* ByteBack — two smaller ventures */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a href="https://www.bytebackgaming.com" target="_blank" rel="noopener noreferrer"
                data-testid="footer-byteback-gaming-card"
                onClick={() => trackCrossPromo("byteback_gaming", placement)}
                className="group block rounded-xl p-3 border border-[#e11d1d]/25 bg-gradient-to-br from-[#1a0d0d] to-donkey-black transition-all duration-300 hover:-translate-y-0.5 hover:border-[#ef4444]/70 hover:shadow-[0_8px_24px_rgba(225,29,29,0.16)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-donkey-orange focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-black">
                <img src="/brand/byteback-gaming.jpg" alt="ByteBack Gaming logo" className="w-9 h-9 rounded-full mb-2" />
                <p className="text-xs font-heading font-semibold text-donkey-warm">ByteBack Gaming</p>
                <p className="text-[11px] text-donkey-grey leading-snug mt-0.5">Custom retro gaming systems built for thousands of games.</p>
                <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[#f87171] group-hover:text-[#fca5a5] transition-colors">
                  Visit <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" /> <ExternalLink className="w-2.5 h-2.5" />
                </span>
              </a>
              <a href="https://www.bytebackarcade.com" target="_blank" rel="noopener noreferrer"
                data-testid="footer-byteback-arcade-card"
                onClick={() => trackCrossPromo("byteback_arcade", placement)}
                className="group block rounded-xl p-3 border border-[#e11d1d]/25 bg-gradient-to-br from-[#1a0d0d] to-donkey-black transition-all duration-300 hover:-translate-y-0.5 hover:border-[#ef4444]/70 hover:shadow-[0_8px_24px_rgba(225,29,29,0.16)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-donkey-orange focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-black">
                <img src="/brand/byteback-arcade.png" alt="ByteBack Arcade logo" className="w-9 h-9 rounded-full mb-2 bg-black object-contain" />
                <p className="text-xs font-heading font-semibold text-donkey-warm">ByteBack Arcade</p>
                <p className="text-[11px] text-donkey-grey leading-snug mt-0.5">Play thousands of classic games online across your devices.</p>
                <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[#f87171] group-hover:text-[#fca5a5] transition-colors">
                  Visit <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" /> <ExternalLink className="w-2.5 h-2.5" />
                </span>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-donkey-line flex flex-col md:flex-row justify-between gap-4">
          <p className="text-xs text-donkey-grey">© {year} The Honest Donkey. All rights reserved.</p>
          <p className="text-xs text-donkey-grey max-w-2xl md:text-right">
            The Honest Donkey provides research and decision support, not financial, legal or professional advice.
            All opportunities require independent validation.
          </p>
        </div>
      </div>
    </footer>
  );
}
